/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DegreePlan_LearnerAdmissionStatus as DegreePlanLearnerAdmissionStatus } from '__generated__/graphql-types';

import { Hidden, LoadingSection } from '@coursera/cds-core';

import useHandleDocumentVisibility from 'bundles/admin-shared/hooks/useHandleDocumentVisibility';
import ErrorCard from 'bundles/degree-home/components/v3-dpm/components/ErrorCard';
import PlanningToolInitialState from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/InitialState';
import ToolRenderer from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/ToolRenderer';
import type { DegreePlan_TermPlanFragment as TermPlan } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/learnerPlanFragment';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import _t from 'i18n!nls/degree-home';

const GRAPHQL_NOT_FOUND_CODE = 'NOT_FOUND';

const styles = {
  cardContainer: css`
    background: var(--cds-color-neutral-background-primary-weak);
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-400);
    border: none;
  `,
};

const PlanningTool = ({ learnerPlan }: { learnerPlan: TermPlan[] | null | undefined }) => {
  const userHasNotCreatedADegreePlan = !learnerPlan;

  return (
    <Card css={styles.cardContainer}>
      {userHasNotCreatedADegreePlan ? <PlanningToolInitialState /> : <ToolRenderer />}
    </Card>
  );
};

const PlanningToolContainer = () => {
  const degreePlanGql = useDegreePlan();
  const curriculumGql = useCurriculum();

  const learnerPlan = degreePlanGql.data?.LearnerPlan?.queryPlan?.learnerPlan;
  const isLearnerNotEnrolled =
    curriculumGql?.data?.Degree?.queryById?.curriculum?.learnerAdmissionStatus ===
    DegreePlanLearnerAdmissionStatus.NotEnrolled;

  // when a learner plan is not found show planning tool in its initial state so that a learner can create a plan
  const hasNotFoundError = degreePlanGql.error?.graphQLErrors?.[0]?.extensions?.errorType === GRAPHQL_NOT_FOUND_CODE;

  useHandleDocumentVisibility({ onDocumentVisible: degreePlanGql.refetch });

  // only show error card when the gql error doesn't include a NOT_FOUND error type
  const showErrorCard = degreePlanGql.error && !hasNotFoundError;
  const showPlanningTool = degreePlanGql.data || hasNotFoundError;

  return (
    <Hidden smDown>
      {degreePlanGql.loading && !degreePlanGql.data && (
        <Card>
          <LoadingSection label={_t('Fetching data...')} />
        </Card>
      )}
      {(showErrorCard || isLearnerNotEnrolled) && <ErrorCard />}
      {showPlanningTool && !isLearnerNotEnrolled && <PlanningTool learnerPlan={learnerPlan} />}
    </Hidden>
  );
};

export default PlanningToolContainer;
